/**
 * École Nid Douillet - CMS Manager Component
 * 
 * Content Management System admin interface for directors
 */

import React, { useState, useEffect } from 'react';
import { 
  cmsAdminApi,
  CMSContent,
  CreateContentRequest,
  CMSStats
} from '@/services/cms.api';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  EyeIcon,
  DocumentTextIcon,
  NewspaperIcon,
  MegaphoneIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

interface CMSManagerProps {
  className?: string;
}

const CMSManager: React.FC<CMSManagerProps> = ({ className = '' }) => {
  const [contentList, setContentList] = useState<CMSContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'pages' | 'posts' | 'news' | 'announcements'>('all');
  const [showEditor, setShowEditor] = useState(false);
  const [editingContent, setEditingContent] = useState<CMSContent | null>(null);
  const [formData, setFormData] = useState<CreateContentRequest>({
    title: '',
    slug: '',
    content_type: 'page',
    language: 'fr',
    excerpt: '',
    content: '',
    meta_title: '',
    meta_description: '',
    featured_image: '',
    tags: [],
    is_published: false,
    display_order: 0
  });

  // Load content
  useEffect(() => {
    loadContent();
  }, [activeTab]);

  const loadContent = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = activeTab !== 'all' ? { type: activeTab as any } : {};
      const response = await cmsAdminApi.getAllContent(params);

      if (response.success && response.data) {
        setContentList(response.data.content);
      } else {
        setError(response.message || 'Erreur lors du chargement du contenu');
      }
    } catch (err) {
      console.error('Error loading content:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError(null);
      
      // Validate form
      const errors = cmsAdminApi.validateContent(formData);
      if (errors.length > 0) {
        setError(errors.join(', '));
        return;
      }

      let response;
      if (editingContent) {
        response = await cmsAdminApi.updateContent(editingContent.id, formData);
      } else {
        response = await cmsAdminApi.createContent(formData);
      }

      if (response.success) {
        setShowEditor(false);
        setEditingContent(null);
        resetForm();
        loadContent();
      } else {
        setError(response.message || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      console.error('Error saving content:', err);
      setError('Erreur lors de la sauvegarde');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      content_type: 'page',
      language: 'fr',
      excerpt: '',
      content: '',
      meta_title: '',
      meta_description: '',
      featured_image: '',
      tags: [],
      is_published: false,
      display_order: 0
    });
  };

  // Handle edit
  const handleEdit = (content: CMSContent) => {
    setEditingContent(content);
    setFormData({
      title: content.title,
      slug: content.slug,
      content_type: content.content_type,
      language: content.language,
      excerpt: content.excerpt || '',
      content: content.content,
      meta_title: content.meta_title || '',
      meta_description: content.meta_description || '',
      featured_image: content.featured_image || '',
      tags: content.tags || [],
      is_published: content.is_published,
      display_order: content.display_order
    });
    setShowEditor(true);
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce contenu ?')) {
      return;
    }

    try {
      const response = await cmsAdminApi.deleteContent(id);
      if (response.success) {
        loadContent();
      } else {
        setError(response.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('Error deleting content:', err);
      setError('Erreur lors de la suppression');
    }
  };

  // Handle publish toggle
  const handlePublishToggle = async (content: CMSContent) => {
    try {
      const response = content.is_published 
        ? await cmsAdminApi.unpublishContent(content.id)
        : await cmsAdminApi.publishContent(content.id);

      if (response.success) {
        loadContent();
      } else {
        setError(response.message || 'Erreur lors de la modification');
      }
    } catch (err) {
      console.error('Error toggling publish status:', err);
      setError('Erreur lors de la modification');
    }
  };

  // Generate slug from title
  const handleTitleChange = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title,
      slug: prev.slug || cmsAdminApi.generateSlug(title)
    }));
  };

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'page': return <DocumentTextIcon className="h-5 w-5" />;
      case 'post': return <PencilIcon className="h-5 w-5" />;
      case 'news': return <NewspaperIcon className="h-5 w-5" />;
      case 'announcement': return <MegaphoneIcon className="h-5 w-5" />;
      default: return <DocumentTextIcon className="h-5 w-5" />;
    }
  };

  if (showEditor) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-neutral-900">
              {editingContent ? 'Modifier le contenu' : 'Nouveau contenu'}
            </h2>
            <button
              onClick={() => {
                setShowEditor(false);
                setEditingContent(null);
                resetForm();
              }}
              className="text-neutral-500 hover:text-neutral-700"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Titre *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Slug *
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Type de contenu *
                </label>
                <select
                  value={formData.content_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, content_type: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="page">Page</option>
                  <option value="post">Article</option>
                  <option value="news">Actualité</option>
                  <option value="announcement">Annonce</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Langue
                </label>
                <select
                  value={formData.language}
                  onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="fr">Français</option>
                  <option value="ar">Arabe</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Extrait
              </label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Résumé du contenu..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Contenu *
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                rows={12}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Contenu HTML..."
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Titre SEO
                </label>
                <input
                  type="text"
                  value={formData.meta_title}
                  onChange={(e) => setFormData(prev => ({ ...prev, meta_title: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  maxLength={60}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Description SEO
                </label>
                <input
                  type="text"
                  value={formData.meta_description}
                  onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  maxLength={160}
                />
              </div>
            </div>

            <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
                  className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-neutral-700">Publié</span>
              </label>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Ordre d'affichage
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  className="w-20 px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  min="0"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  setShowEditor(false);
                  setEditingContent(null);
                  resetForm();
                }}
                className="px-6 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                {editingContent ? 'Mettre à jour' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <GlobeAltIcon className="h-8 w-8 text-primary-600" />
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">
                Gestion du Contenu
              </h1>
              <p className="text-neutral-600">
                Créez et gérez le contenu du site web
              </p>
            </div>
          </div>
          
          <button
            onClick={() => setShowEditor(true)}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nouveau contenu
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-neutral-100 rounded-lg p-1">
          {[
            { key: 'all', label: 'Tout', icon: DocumentTextIcon },
            { key: 'pages', label: 'Pages', icon: DocumentTextIcon },
            { key: 'posts', label: 'Articles', icon: PencilIcon },
            { key: 'news', label: 'Actualités', icon: NewspaperIcon },
            { key: 'announcements', label: 'Annonces', icon: MegaphoneIcon }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content List */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : contentList.length === 0 ? (
          <div className="text-center p-8">
            <DocumentTextIcon className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 mb-2">
              Aucun contenu
            </h3>
            <p className="text-neutral-600">
              Commencez par créer votre premier contenu.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Contenu
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Vues
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Modifié
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {contentList.map((content) => (
                  <tr key={content.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {getContentTypeIcon(content.content_type)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-neutral-900">
                            {content.title}
                          </div>
                          <div className="text-sm text-neutral-500">
                            /{content.slug}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-800">
                        {content.content_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handlePublishToggle(content)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          content.is_published
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {content.is_published ? 'Publié' : 'Brouillon'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      {content.view_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                      {new Date(content.updated_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => window.open(`/${content.slug}`, '_blank')}
                          className="text-neutral-400 hover:text-neutral-600"
                          title="Voir"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(content)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Modifier"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(content.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}
    </div>
  );
};

export default CMSManager;
